import {AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {ToastrService} from 'ngx-toastr';
import {GeneralService} from 'src/app/services/general.service';
import {EmailClientService} from 'src/app/services/integrations/email/email-client.service';
import {EmailService} from 'src/app/services/integrations/email/email.service';
import {LeadsCustomersService} from 'src/app/services/settings-services/leads-customers.service';
import {AppState} from 'src/app/store/app.state';
import {environment, Office365OuthSettings} from 'src/environments/environment';
import EmailAnimations from './animations';
import EmailAttachmentHelper from './email-attachment';
import EmailDeals from './email-deal';
import EmailHelper from './email-helper';
import {TicketBulkService} from 'src/app/services/settings-services/ticket-bulk.service';

@Component({
  selector: 'app-email',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.css'],
})
export class EmailComponent implements OnInit, OnDestroy, AfterViewInit {
  /** */
  @Input() component_id: any;

  html_ids: any = {};
  modal_is_opened: boolean = false;

  /** */
  userId: number;
  orgId: number;
  orgName: string;
  user_data: any;

  /** FOR QUOTE/INVOICE SENDING */
  sales_item: any;
  has_sales_item: boolean = false;

  formMessage = {
    class: '',
    message: '',
  };
  emailSignature = {
    'emailSignature': '',
    'isEnabled': true
  };
  response: object;
  current_uri: string;

  /** FOR PROGRESS LOADERS */
  account_to_connect: string = '';
  connecting = {
    gmail: false,
    office: false,
    imap: false,
  };

  serviceDisconnecting: boolean = false;

  mailSending: boolean = false;

  /** FOR EMAIL VIEWING */
  currentlyOpenedMail: any = {};
  currentlyOpenedMailExists: boolean = false;
  context_name: string;

  mailIsReply: boolean;
  mailSubject: string;
  mailBody: string;
  mailDeal: string = '';

  /**
   * EMAIL HELPERS
   */
  emailHelper;
  attachmentHelper;
  emailAnimations;
  emailDeals;

  mailingUrl = environment.mailServiceUrl;
  email_domain = environment.emailDomain;
  isConnecting: boolean = false;
  isSignatureEnabled: any;
  signature: any;

  private modalShowHandler: () => void;
  private tabShowHandler: (e: Event) => void;
  private parentModal: Element;

  constructor(
    private generalService: GeneralService,
    private emailService: EmailService,
    private emailClientService: EmailClientService,
    private leadCustomerService: LeadsCustomersService,
    private store: Store<AppState>,
    private toast: ToastrService,
    private ticketBulk: TicketBulkService,
    private elRef: ElementRef,
    private ngZone: NgZone,
  ) {
    this.emailHelper = EmailHelper;
    this.attachmentHelper = new EmailAttachmentHelper(
      emailService,
      emailService.getAttachmentUrl(),
      toast
    );
    this.emailDeals = new EmailDeals(generalService, emailService);

    //
    this.store.select('userInfo').subscribe((info) => {
      if (Object.keys(info).length) {
        this.userId = info.user.id;
        this.orgId = info.organization.id;
        this.orgName = info.organization.name.replace(/(\s)/gi, '');
      }
    });
  }

  ngAfterViewInit(): void {
    this.emailAnimations.loadFormElements();
    this.applySignatureOnOpen();
    this.parentModal = this.elRef.nativeElement.closest('.modal');
    if (this.parentModal) {
      this.modalShowHandler = () => this.ngZone.run(() => this.applySignatureOnOpen());
      this.parentModal.addEventListener('shown.bs.modal', this.modalShowHandler);
    } else {
      const parentTabPane = this.elRef.nativeElement.closest('.tab-pane');
      if (parentTabPane && parentTabPane.id) {
        const targetHref = '#' + parentTabPane.id;
        this.tabShowHandler = (e: Event) => {
          const anchor = e.target as HTMLAnchorElement;
          if (anchor && anchor.getAttribute('href') === targetHref) {
            this.ngZone.run(() => this.applySignatureOnOpen());
          }
        };
        this.applySignatureOnOpen();
        document.addEventListener('shown.bs.tab', this.tabShowHandler);
      }
    }
  }

  ngOnInit() {
    this.fetchEmailSignature();
    window.addEventListener('pinc:conn:pass', () => this.save_sso_data_to_db());
    window.addEventListener(
      'pinc:conn:fail',
      () => (this.connecting[this.account_to_connect] = false)
    );

    this.retrieve_connected_account();

    this.emailService.user_data.subscribe((data) => {
      this.user_data = {...data};
    });

    this.emailService.context_primary_email_address.subscribe(
      (email_address) => {
        this.emailHelper.inputTo = email_address;
      }
    );

    this.emailService.email_context.subscribe((data) => {
      if (!this.generalService.checkIfObjectIsEmpty(data)) {
        this.context_name = data.name.toLowerCase();

        if (this.context_name == 'dashboard') {
          this.emailHelper.inputTo = '';
        }
        if (this.context_name == 'deal') {
          this.emailDeals.selectDeal(data.deal);
        } else {
          this.emailDeals.clearSuggestions();
        }
      }
    });

    this.emailService.sales_item.subscribe((data) => {
      if (!this.generalService.checkIfObjectIsEmpty(data)) {
        this.resetForm();
        const item_type =
          data.type.toLowerCase() === 'quote' ? 'quotation' : 'invoice';
        this.has_sales_item = true;
        this.sales_item = {
          type: item_type,
          id: data.payload[item_type].id,
          payload: data.payload,
        };

        this.emailHelper.inputTo = data.payload.client.email;
        document.getElementById('header-nav-new-mail-btn').click();
      } else {
        this.has_sales_item = false;
        this.sales_item = undefined;
      }

    });

    this.emailAnimations = new EmailAnimations(this.component_id);

    this.html_ids = {
      email_area: `email-area-${this.component_id}`,
      input_cc: `input-cc-${this.component_id}`,
      input_cc_list: `input-cc-list-${this.component_id}`,
      input_bcc: `input-bcc-${this.component_id}`,
      input_bcc_list: `input-bcc-list-${this.component_id}`,
      file_upload_btn: `fileUploadBtn-${this.component_id}`,
      email_deal: `email-deal-${this.component_id}`,
    };

    this.ticketBulk.emailSignature$.subscribe((data) => {
      if (data) {
        this.signature = data.emailSignature;
        this.isSignatureEnabled = data.isEnabled;
        this.emailSignature.isEnabled = data.isEnabled;
      }
    });

    this.emailService.selectedEmail.subscribe(
      (email) => {
        if (this.user_data.service !== email.service) {
          this.displayFormMessage(
            0,
            `You cannot reply mail from a disconnected mailbox`
          );
          return;
        }

        if (!this.generalService.checkIfObjectIsEmpty(email)) {
          this.mailIsReply = true;
          this.currentlyOpenedMail =
            this.emailHelper.processCurrentlyOpenedMail(
              email,
              this.user_data.serviceKey
            );

          this.emailHelper.processReplySelection(
            'selectedCcRecipients',
            email.body.cc
          );
          this.emailHelper.processReplySelection(
            'selectedBccRecipients',
            email.body.bcc
          );

          this.mailSubject = this.currentlyOpenedMail.subject;
          this.emailHelper.inputTo = this.currentlyOpenedMail.from;
          this.mailBody = this.currentlyOpenedMail.body;
          this.emailDeals.mailDeal = email.deal ? email.deal.name : '';

          this.emailDeals.selected_deal = email.deal || {};
          this.currentlyOpenedMailExists = true;
        }
      },
      (error) => {
      }
    );

    this.emailAnimations.loadFormElements();
  }

  ngOnDestroy() {
    this.resetForm();
    if (this.modalShowHandler && this.parentModal) {
      this.parentModal.removeEventListener('shown.bs.modal', this.modalShowHandler);
    }
    if (this.tabShowHandler) {
      document.removeEventListener('shown.bs.tab', this.tabShowHandler);
    }
  }

  requestAccessToken(service: string) {
    this.isConnecting = true;
    this.connecting[service] = true;
    this.connectMail(service);

  }


  connectMail(service: any): void {
    const authUrl = `${this.mailingUrl}/users/authorize/${service}?userId=${this.userId}&orgId=${this.orgId}`;
    const oauthWindow = this.openOAuthPopup(authUrl, `${service} OAuth`);

    if (oauthWindow) {
      this.pollForOAuthCompletion(this.userId, this.orgId, service, oauthWindow);
    } else {
      console.error('Failed to open OAuth popup. Please allow popups for this site.');
      alert('Please allow popups for this site to connect your Gmail account.');
    }
  }

  private openOAuthPopup(url: string, title: string): Window | null {
    const width = 600;
    const height = 700;
    const left = (screen.width / 2) - (width / 2);
    const top = (screen.height / 2) - (height / 2);

    const features = `width=${width},height=${height},left=${left},top=${top},` +
      `toolbar=no,menubar=no,scrollbars=yes,resizable=yes`;

    return window.open(url, title, features);
  }

  /**
   * Poll backend to check if OAuth is complete
   * Automatically closes popup when authentication is successful
   */
  private pollForOAuthCompletion(
    userId: number,
    orgId: number,
    service: 'gmail' | 'office',
    oauthWindow: Window | null
  ): void {
    //use SSE for checking  if the user has been authenticated
    const eventSource = new EventSource(`${this.mailingUrl}/users/oauth/events?userId=${userId}&orgId=${orgId}&service=${service}`);
    eventSource.onmessage = (event) => {
      const result = JSON.parse(event.data);
      eventSource.close();
      if (oauthWindow && !oauthWindow.closed) {
        oauthWindow.close();
      }
      if (result.success) {
        this.emailService.user_data.next({...result.data.payload});
        this.generalService.sweetAlertSucess('Account connected')
          .then(() => {
            window.location.reload();
          });
      } else {
        this.generalService.sweetAlertError('Failed to connect account ' + result.error)
          .then(() => {
            window.location.reload();
          });
      }
      this.isConnecting = false;
    };

    eventSource.onerror = (error) => {
      console.error('SSE Error:', error);
      eventSource.close();
      this.isConnecting = false;
    };

    eventSource.onopen = () => {
      console.log('SSE Connection opened');
    };


  }

  setLookUpGroup(group: string) {
    this.emailClientService.recipient_group.next(group);
  }

  set_account_to_connect(account_name: string) {
    this.account_to_connect = account_name;
    this.connecting[account_name] = true;
  }

  retrieve_connected_account() {
    this.emailService.fetchAccessTokenDataFromServer().subscribe(
      (response: any) => {
        const {payload} = response;
        if (payload[0] && payload[0].is_active) {
          //
          this.emailService.user_data.next({
            ...payload[0],
          });
        } else {
          //
          this.emailService.user_data.next({});
        }
      },
      (err) => {
      }
    );
  }

  save_sso_data_to_db(): any {
    const data = {
      service: this.account_to_connect,
      user_id: this.userId,
      org_id: this.orgId,
    };

    this.connecting[this.account_to_connect] = true;
    this.emailService.saveAccessTokenOnServer({...data}).subscribe(
      (response: any) => {
        this.connecting[this.account_to_connect] = false;
        if (response.success) {
          this.emailService.user_data.next({...response.payload});
          this.account_to_connect = '';
          this.toast.success('Account connected');
          this.handle_email_lead_source_creation();
        } else {
          this.toast.error('Could not connect account.');
        }
      },
      (error) => {
        this.connecting[this.account_to_connect] = false;
      }
    );
  }

  revokeAccess() {
    const service = this.user_data.service;
    this.generalService
      .sweetAlertContinue('Disconnect account')
      .then((response: any) => {
        if (response.value) {
          this.serviceDisconnecting = true;
          this.emailService.revokeUserAccess(this.user_data.id).subscribe(
            (response: any) => {
              this.serviceDisconnecting = false;
              if (response.success) {
                this.emailService.user_data.next({});
                this.toast.success('Account disconnected.');
                window.location.reload();
                console.log('reloading');
                if (service === 'office') {
                  // this.Office365SignOut();
                }

              } else {
                this.toast.error(`Could not disconnect account`);
              }
              window.location.reload();
            },
            (error) => {
              this.serviceDisconnecting = false;
            }
          );
        }
      });
  }

  Office365SignOut() {
    const {authority, redirectUri} = Office365OuthSettings;
    localStorage.setItem('redirect_uri', window.location.pathname);
    window.location.href = `${authority}/oauth2/logout?post_logout_redirect_uri=${redirectUri}`;
  }

  fetchEmailSignature() {
    this.ticketBulk.getEmailSignature().subscribe((res: any) => {
      if (res && res.data) {
        this.signature = res.data.emailSignature;
        this.isSignatureEnabled = res.data.isEnabled;
        this.emailSignature.isEnabled = res.data.isEnabled;
        this.emailSignature.emailSignature = res.data.emailSignature;
      } else {
        this.signature = '';
      }
    });
  }

  applySignatureOnOpen() {
    if (this.mailIsReply) {
      return;
    }
    const setEditorContent = (body: string) => {
      this.mailBody = body || null;
      if (this.emailAnimations.quillEditor && body) {
        this.emailAnimations.quillEditor.clipboard.dangerouslyPasteHTML(body);
      }
    };
    if (this.signature !== undefined) {
      setEditorContent(this.mailBody || this.getSignatureBody());
    } else {
      this.ticketBulk.getEmailSignature().subscribe((res: any) => {
        if (res && res.data) {
          this.signature = res.data.emailSignature;
          this.isSignatureEnabled = res.data.isEnabled;
          this.emailSignature.isEnabled = res.data.isEnabled;
          this.emailSignature.emailSignature = res.data.emailSignature;
        } else {
          this.signature = '';
        }
        setEditorContent(this.mailBody || this.getSignatureBody());
      });
    }
  }


  getSignatureBody(): string {
    return this.signature && this.isSignatureEnabled
      ? `<br><br><br>${this.signature}`
      : '';
  }

  /** */
  checkIfFieldIsEmpty() {
    if (!this.user_data.service) {
      this.displayFormMessage(0, `No email account is connected`);
      return true;
    }

    const emailPattern = /^(\s*[^\s@]+@[^\s@]+\.[^\s@]+\s*(,\s*[^\s@]+@[^\s@]+\.[^\s@]+\s*)*)*$/;

    if (!emailPattern.test(this.emailHelper.inputTo.split(',').map(email => email.trim()))) {
      this.displayFormMessage(0, `Enter a valid recipient`);
      return true;
    }

    if (this.mailSubject === undefined || this.mailSubject.length <= 0) {
      this.displayFormMessage(0, `Subject cannot be empty`);
      return true;
    }

    if (!this.mailBody) {
      this.displayFormMessage(0, `Mail body cannot be empty`);
      return true;
    }

    return false;
  }

  getAllBcc() {
    return this.emailHelper.allBccEmails.join(',');
  }

  getAllCc() {
    return this.emailHelper.allCcEmails.join(',');
  }

  validateCopiedEmails() {
    const emailPattern =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const emails = [
      ...this.getAllBcc().split(','),
      ...this.getAllCc().split(','),
    ];

    for (let i in emails) {
      const email = emails[i].trim();
      if (!email) {
        continue;
      }
      if (!emailPattern.test(email)) {
        this.displayFormMessage(0, `Invalid email: ${email}`);
        return false;
      }
    }
    return true;
  }

  discard() {
    this.generalService
      .sweetAlertContinue('Discard mail')
      .then((response: any) => {
        if (response.value) {
          this.resetForm();
        }
      });
  }

  resetForm() {
    this.mailBody = this.emailSignature.isEnabled ? this.getSignatureBody() : '';
    this.mailSubject = '';
    this.mailIsReply = false;
    this.emailHelper.inputCc = '';
    this.emailHelper.inputBcc = '';
    this.emailHelper.inputTo = '';
    this.emailHelper.clearTemporarySelections();
    this.emailHelper.clearSelectedRecipients();
    this.emailDeals.clearSuggestions();
    this.attachmentHelper.selectedAttachments = {};
    this.emailHelper.allCcEmails = [];
    this.emailHelper.allBccEmails = [];
    this.removeSalesItem();
  }

  /** Removes a sales Item (invoice or uotation attached to the email component) */
  removeSalesItem() {
    this.has_sales_item = false;
    this.sales_item = undefined;
    this.emailService.sales_item.next({});
  }

  processAttachmentsForSending() {
    const attachments: Array<any> =
      this.attachmentHelper.reduceSelectedAttachmentsToList();
    let attachment_string = '';
    if (!attachments.length) {
      return attachment_string;
    }

    let result = {
      files: [],
      urls: [],
    };

    // let total_size = 0;
    attachments.forEach((attachment) => {
      // total_size += attachment.size;
      // if (total_size > (3.8 * 1024 * 1024)) {
      //   result.urls.push(attachment);
      //   return
      // }

      if (attachment.encoded) {
        result.files.push(attachment);
      } else {
        result.urls.push(attachment);
      }
    });

    return {
      text: `[ATTACHMENTS] <br><br> ${result.urls
        .map(
          (attachment, i) =>
            `<a href='${attachment.url}'>${i + 1} ${attachment.name}</a>`
        )
        .join('<br>')}`,
      data: attachments,
    };
  }

  processMailBody(body) {
    const pattern1 = /\r\n/;
    const pattern2 = /\n/;
    if (pattern1.test(body)) {
      return body.replace(/\r\n/g, '<br/>');
    } else if (pattern2.test(body)) {
      return body.replace(/\n/g, '<br/>');
    } else {
      return body;
    }
  }

  sendMail() {
    if (this.checkIfFieldIsEmpty()) {
      return;
    }

    if (!this.validateCopiedEmails()) {
      return;
    }

    this.displayFormMessage(3, `Sending Mail`);

    this.emailHelper.processInputResidue();
    if (this.emailHelper.inputTo == this.currentlyOpenedMail.from) {
      this.mailIsReply = true;
    }

    let replyMailBody: any = {};
    this.mailIsReply =
      this.mailIsReply && this.mailSubject === this.currentlyOpenedMail.subject;
    if (this.mailIsReply) {
      const headers = this.currentlyOpenedMail.headers
        ? this.currentlyOpenedMail.headers
        : this.currentlyOpenedMail.body.headers;
      replyMailBody = {
        headers,
        prevBody: this.currentlyOpenedMail.body,
        dealId: this.currentlyOpenedMail.dealId,
        thread_db_id: this.currentlyOpenedMail.thread_db_id,
        threadId: this.currentlyOpenedMail.thread_id,
        messageId: this.currentlyOpenedMail.message_id,
      };
    }

    const mailBody = this.processMailBody(this.mailBody);
    const attachments: any = this.processAttachmentsForSending();

    const message = {
      ...replyMailBody,
      bcc: this.getAllBcc(),
      body: mailBody, //+ `<br><br><br> ${attachments.text || ''}`,),
      cc: this.getAllCc(),
      deal: this.emailDeals.selected_deal,
      sales_item: this.sales_item,
      sender: this.user_data,
      subject: this.mailSubject,
      to: this.emailHelper.inputTo.trim(),
      type: this.mailIsReply ? 'reply' : undefined,
      attachments: attachments.data,
    };

    delete message.request;
    delete message._id;

    this.toggleMailSendingLoader();

    if (this.has_sales_item) {
      this.emailService
        .sendSalesItemMail(message, this.sales_item.type)
        .subscribe(
          (response: any) => {
            this.processEmailSendResponse(response);
          },
          (error) => {
            this.displayFormMessage(0, `Error: Sending failed.`);
            this.toggleMailSendingLoader();
          }
        );
    } else {
      this.emailService.sendMail(message, this.user_data.service).subscribe(
        (response: any) => {
          this.processEmailSendResponse(response);
        },
        (error) => {
          this.displayFormMessage(0, `Error: Sending failed.`);
          this.toggleMailSendingLoader();
        }
      );
    }
  }

  //
  handle_email_lead_source_creation() {
    this.leadCustomerService
      .filter_organisation_lead_sources(`name=Email`)
      .subscribe((response: any) => {
        if (response.success && !response.payload.length) {
          this.leadCustomerService
            .create_organisation_lead_source({name: 'Email'})
            .subscribe(() => {
            });
        }
      });
  }

  /** process server response from an email send */
  processEmailSendResponse(response) {
    this.toggleMailSendingLoader();
    if (response.success) {
      this.displayFormMessage(1, `Message Sent.`);
      this.resetForm();
      this.emailHelper.clearSelectedRecipients();
      this.closeModal();
    } else {
      this.displayFormMessage(0, `Error: Sending failed.`);
    }
  }

  closeModal() {
    this.generalService.sweetAlertNavigate('Email Sent!, Do you want to close email window?')
      .then((response: any) => {
        if (!response.value) {
          return;
        }

        const item = document.getElementById('Close');
        item.click();
      });

  }

  gmailConnected() {
    return this.user_data.is_active && this.user_data.service === 'gmail';
  }

  imapConnected() {
    return this.user_data.is_active && this.user_data.service === 'imap';
  }

  officeConnected() {
    return this.user_data.is_active && this.user_data.service === 'office';
  }

  /**
   *
   *
   *
   *
   *
   */

  openFileUploadDialog() {
    const fileUploadBtn = document.getElementById(
      this.html_ids.file_upload_btn
    );
    fileUploadBtn.click();
  }

  toggleMailSendingLoader() {
    this.mailSending = !this.mailSending;
  }

  flattenEmailRecipients(recipients) {
    const recipientMails = recipients.map((recipient) => recipient.address);
    return recipientMails.join();
  }

  determineAlertClass(code) {
    switch (code) {
      case 0: // error
        return `alert alert-danger`;
      case 1: // success
        return `alert alert-success`;
      default:
        return `alert alert-info`;
    }
  }

  displayFormMessage(code, message) {
    this.formMessage.class = this.determineAlertClass(code);
    this.formMessage.message = message;
    setTimeout(() => {
      this.formMessage = {
        class: "",
        message: "",
      };
    }, 2500);
  }
}
